<script lang="ts">
  interface Channel {
    id: number
    youtube_channel_id: string
    name: string
    min_duration_minutes: number
    enabled: boolean
    last_polled_at: string | null
    last_poll_failed: boolean
    video_count: number
  }

  let channels = $state<Channel[]>([])
  let loading = $state(true)
  let error = $state('')
  let notice = $state('')

  let newName = $state('')
  let newChannelId = $state('')
  let newMinutes = $state(20)
  let creating = $state(false)

  let pollIntervalHours = $state(24)
  let savingInterval = $state(false)

  let enabledCount = $derived(channels.filter(c => c.enabled).length)
  let perChannelMinutes = $derived(enabledCount > 0 ? Math.round((pollIntervalHours * 60) / enabledCount) : 0)

  $effect(() => {
    fetchChannels()
    fetchSettings()
  })

  async function fetchSettings() {
    try {
      const response = await fetch('/api/settings', { credentials: 'same-origin' })
      if (response.ok) {
        const data = await response.json()
        pollIntervalHours = data.poll_interval_hours
      }
    } catch (e) {
      // leave the default in place when settings cannot be loaded
    }
  }

  async function saveInterval(input: HTMLInputElement) {
    const hours = Math.max(1, parseInt(input.value, 10) || 0)
    if (hours === pollIntervalHours) return
    savingInterval = true
    error = ''
    try {
      const response = await fetch('/api/settings', {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ setting: { poll_interval_hours: hours } }),
      })
      if (response.ok) {
        const data = await response.json()
        pollIntervalHours = data.poll_interval_hours
      } else {
        const data = await response.json().catch(() => ({}))
        error = (data.errors || ['Failed to update polling interval']).join(', ')
        input.value = String(pollIntervalHours)
      }
    } catch (e) {
      error = 'Network error'
      input.value = String(pollIntervalHours)
    }
    savingInterval = false
  }

  function formatSpacing(minutes: number): string {
    if (minutes <= 0) return 'no enabled channels'
    if (minutes < 60) return `one channel every ${minutes} min`
    const hours = Math.floor(minutes / 60)
    const rest = minutes % 60
    return rest === 0 ? `one channel every ${hours}h` : `one channel every ${hours}h ${rest}m`
  }

  async function fetchChannels() {
    error = ''
    try {
      const response = await fetch('/api/channels', { credentials: 'same-origin' })
      if (response.ok) {
        channels = await response.json()
      } else if (response.status === 401 || response.status === 403) {
        error = 'You need the manage-videos permission to manage channels.'
      } else {
        error = 'Failed to load channels'
      }
    } catch (e) {
      error = 'Network error'
    }
    loading = false
  }

  async function createChannel() {
    if (!newChannelId.trim() || creating) return
    creating = true
    error = ''
    try {
      const response = await fetch('/api/channels', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          channel: {
            name: newName.trim(),
            youtube_channel_id: newChannelId.trim(),
            min_duration_minutes: newMinutes,
          },
        }),
      })
      if (response.ok) {
        const channel = await response.json()
        channels = [...channels, channel].sort((a, b) => a.name.localeCompare(b.name))
        newName = ''
        newChannelId = ''
        newMinutes = 20
      } else {
        const data = await response.json().catch(() => ({}))
        error = (data.errors || ['Failed to add channel']).join(', ')
      }
    } catch (e) {
      error = 'Network error'
    }
    creating = false
  }

  async function patchChannel(channel: Channel, attrs: Record<string, unknown>): Promise<boolean> {
    try {
      const response = await fetch(`/api/channels/${channel.id}`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel: attrs }),
      })
      if (response.ok) {
        const updated = await response.json()
        channels = channels.map(c => (c.id === updated.id ? updated : c))
        error = ''
        return true
      }
      const data = await response.json().catch(() => ({}))
      error = (data.errors || ['Failed to update channel']).join(', ')
      return false
    } catch (e) {
      error = 'Network error'
      return false
    }
  }

  async function toggleEnabled(channel: Channel, input: HTMLInputElement) {
    const ok = await patchChannel(channel, { enabled: input.checked })
    if (!ok) input.checked = channel.enabled
  }

  async function deleteChannel(channel: Channel) {
    if (!confirm(`Remove ${channel.name} from the ingest list?`)) return
    try {
      const response = await fetch(`/api/channels/${channel.id}`, {
        method: 'DELETE',
        credentials: 'same-origin',
      })
      if (response.ok) {
        channels = channels.filter(c => c.id !== channel.id)
      }
    } catch (e) {
      error = 'Network error'
    }
  }

  let pollingId = $state<number | null>(null)
  let pollingAll = $state(false)

  async function pollChannel(channel: Channel) {
    pollingId = channel.id
    error = ''
    try {
      const response = await fetch(`/api/channels/${channel.id}/poll`, { method: 'POST', credentials: 'same-origin' })
      notice = response.ok ? `Poll queued for ${channel.name}; new long-form uploads appear within a few minutes.` : ''
    } catch (e) {
      error = 'Network error'
    }
    pollingId = null
  }

  async function pollAll() {
    pollingAll = true
    error = ''
    try {
      const response = await fetch('/api/channels/poll', { method: 'POST', credentials: 'same-origin' })
      notice = response.ok ? 'Poll queued for every enabled channel.' : ''
    } catch (e) {
      error = 'Network error'
    }
    pollingAll = false
  }

  async function onMinutesChange(channel: Channel, input: HTMLInputElement) {
    const minutes = Math.max(0, parseInt(input.value, 10) || 0)
    if (minutes === channel.min_duration_minutes) return
    const ok = await patchChannel(channel, { min_duration_minutes: minutes })
    if (!ok) input.value = String(channel.min_duration_minutes)
  }

  function formatLastPolled(value: string | null): string {
    if (!value) return 'never'
    return new Date(value).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
  }
</script>

<div class="studio-console">
  <div class="console-header">
    <div class="header-label">
      <div class="label-primary">INGEST CHANNELS</div>
      <div class="label-secondary">{channels.length} CHANNELS</div>
    </div>
    <div class="header-right">
      <button class="action-btn" onclick={pollAll} disabled={pollingAll}>
        {pollingAll ? 'QUEUEING...' : 'POLL ALL'}
      </button>
      <a href="/admin" class="back-btn"><span class="btn-label">BACK</span></a>
    </div>
  </div>

  <form class="add-form" onsubmit={(e) => { e.preventDefault(); createChannel() }}>
    <input class="field id-field" placeholder="Channel URL, @handle, or UC id" bind:value={newChannelId} />
    <input class="field name-field" placeholder="Name (optional)" bind:value={newName} />
    <label class="minutes-label" title="Minimum video length to ingest">
      ≥
      <input class="field minutes-field" type="number" min="0" bind:value={newMinutes} />
      min
    </label>
    <button class="action-btn" type="submit" disabled={creating}>
      {creating ? 'ADDING...' : 'ADD CHANNEL'}
    </button>
  </form>
  <p class="form-hint">
    Paste the channel's URL (youtube.com/@handle or /channel/UC...), its @handle, or a raw UC id. The name fills
    in automatically when left blank.
  </p>

  <div class="cadence-bar">
    <label class="cadence-label" title="Channels are polled one at a time, spread evenly across this interval">
      POLL EVERY
      <input
        class="field interval-field"
        type="number"
        min="1"
        value={pollIntervalHours}
        disabled={savingInterval}
        onchange={(e) => saveInterval(e.currentTarget as HTMLInputElement)}
      />
      H
    </label>
    <span class="cadence-derived">{formatSpacing(perChannelMinutes)}</span>
  </div>

  {#if error}
    <div class="status-message error">
      <div class="status-indicator"></div>
      <span>{error}</span>
    </div>
  {:else if notice}
    <div class="status-message notice">
      <div class="status-indicator"></div>
      <span>{notice}</span>
    </div>
  {/if}

  {#if loading}
    <div class="status-message">
      <div class="status-indicator pulse"></div>
      <span>LOADING CHANNELS...</span>
    </div>
  {:else if channels.length === 0}
    <div class="status-message">
      <div class="status-indicator"></div>
      <span>NO CHANNELS CONFIGURED</span>
    </div>
  {:else}
    <div class="channel-list">
      {#each channels as channel (channel.id)}
        <div class="channel-strip" class:disabled={!channel.enabled}>
          <label class="toggle" title={channel.enabled ? 'Enabled' : 'Disabled'}>
            <input
              type="checkbox"
              checked={channel.enabled}
              onchange={(e) => toggleEnabled(channel, e.currentTarget as HTMLInputElement)}
            />
            <span class="toggle-track"></span>
          </label>

          <div class="channel-info">
            <div class="channel-name">{channel.name}</div>
            <div class="channel-meta">
              <span class="channel-id">{channel.youtube_channel_id}</span>
              <span>{channel.video_count} ingested</span>
              <span>last polled {formatLastPolled(channel.last_polled_at)}</span>
              {#if channel.last_poll_failed}
                <span
                  class="poll-warning"
                  title="The last poll could not read any video durations, so nothing was ingested. Check the YouTube Data API key or its connectivity."
                >
                  poll failing
                </span>
              {/if}
            </div>
          </div>

          <label class="minutes-label" title="Minimum video length to ingest">
            ≥
            <input
              class="field minutes-field"
              type="number"
              min="0"
              value={channel.min_duration_minutes}
              onchange={(e) => onMinutesChange(channel, e.currentTarget as HTMLInputElement)}
            />
            min
          </label>

          <div class="channel-actions">
            <button class="action-btn" onclick={() => pollChannel(channel)} disabled={pollingId === channel.id}>
              {pollingId === channel.id ? 'QUEUEING...' : 'POLL'}
            </button>
            <button class="danger-btn" onclick={() => deleteChannel(channel)} aria-label="Remove channel">✕</button>
          </div>
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .studio-console {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    background: var(--color-surface-0);
    padding: 2rem;
  }

  .console-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1.5rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid var(--color-border-default);
  }

  .header-label {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .label-primary {
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    color: var(--color-text-primary);
    font-family: var(--font-mono);
  }

  .label-secondary {
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0.15em;
    color: var(--color-text-muted);
    font-family: var(--font-mono);
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .add-form {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
  }

  .field {
    height: 32px;
    padding: 0 0.625rem;
    background: var(--color-surface-2);
    border: 1px solid var(--color-border-default);
    color: var(--color-text-primary);
    font-family: var(--font-mono);
    font-size: 0.75rem;
  }

  .field:focus {
    outline: none;
    border-color: var(--color-accent-primary);
  }

  .name-field { width: 200px; }
  .id-field { width: 240px; }
  .minutes-field { width: 64px; text-align: right; }

  .minutes-label {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-family: var(--font-mono);
    font-size: 0.625rem;
    letter-spacing: 0.1em;
    color: var(--color-text-muted);
    text-transform: uppercase;
  }

  .action-btn,
  .back-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    height: 32px;
    padding: 0 1rem;
    background: var(--color-surface-3);
    border: 1px solid var(--color-border-strong);
    color: var(--color-text-primary);
    font-family: var(--font-mono);
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    cursor: pointer;
    transition: all 0.1s;
    text-decoration: none;
  }

  .action-btn:hover:not(:disabled),
  .back-btn:hover {
    background: var(--color-surface-4);
    border-color: var(--color-accent-primary);
  }

  .action-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .danger-btn {
    height: 32px;
    width: 32px;
    background: var(--color-surface-3);
    border: 1px solid var(--color-border-default);
    color: var(--color-text-muted);
    font-family: var(--font-mono);
    cursor: pointer;
    transition: all 0.1s;
  }

  .danger-btn:hover {
    border-color: var(--color-accent-playhead);
    color: var(--color-accent-playhead);
  }

  .status-message {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.5rem 0;
    font-family: var(--font-mono);
    font-size: 0.8125rem;
    letter-spacing: 0.05em;
    color: var(--color-text-secondary);
  }

  .status-message.error {
    color: var(--color-accent-playhead);
  }

  .status-message.notice {
    color: #4ade80;
  }

  .form-hint {
    margin: -0.75rem 0 1.5rem;
    font-family: var(--font-mono);
    font-size: 0.6875rem;
    color: var(--color-text-muted);
    letter-spacing: 0.02em;
  }

  .form-hint a {
    color: var(--color-text-secondary);
  }

  .cadence-bar {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid var(--color-border-default);
  }

  .cadence-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-family: var(--font-mono);
    font-size: 0.625rem;
    letter-spacing: 0.1em;
    color: var(--color-text-muted);
    text-transform: uppercase;
  }

  .interval-field {
    width: 64px;
    text-align: right;
  }

  .cadence-derived {
    font-family: var(--font-mono);
    font-size: 0.6875rem;
    color: var(--color-text-secondary);
  }

  .status-indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--color-text-muted);
    flex-shrink: 0;
  }

  .status-indicator.pulse {
    background: var(--color-accent-primary);
    animation: pulse 1.5s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 1; }
  }

  .channel-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .channel-strip {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem 1rem;
    background: var(--color-surface-2);
    border: 1px solid var(--color-border-default);
    transition: all 0.1s;
  }

  .channel-strip.disabled {
    opacity: 0.55;
  }

  .channel-strip:hover {
    border-color: var(--color-border-strong);
  }

  .toggle {
    position: relative;
    display: inline-flex;
    cursor: pointer;
    flex-shrink: 0;
  }

  .toggle input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  .toggle-track {
    width: 34px;
    height: 18px;
    border-radius: 9px;
    background: var(--color-surface-4);
    border: 1px solid var(--color-border-strong);
    position: relative;
    transition: all 0.15s;
  }

  .toggle-track::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: var(--color-text-muted);
    transition: all 0.15s;
  }

  .toggle input:checked + .toggle-track {
    background: color-mix(in srgb, var(--color-accent-primary) 35%, transparent);
    border-color: var(--color-accent-primary);
  }

  .toggle input:checked + .toggle-track::after {
    transform: translateX(16px);
    background: var(--color-accent-primary);
  }

  .channel-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .channel-name {
    font-family: var(--font-mono);
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .channel-meta {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    font-family: var(--font-mono);
    font-size: 0.6875rem;
    color: var(--color-text-muted);
  }

  .channel-id {
    color: var(--color-text-secondary);
  }

  .poll-warning {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    color: var(--color-accent-warning);
  }

  .poll-warning::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--color-accent-warning);
  }

  .channel-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
  }
</style>
